import * as React from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { CmdAlert } from '@commander-services/gui-components';
import { useIntl } from 'react-intl';
import { useQueries } from '@tanstack/react-query';
import {
  GRAPH_LIMIT,
  IGraphData,
  IGraphResponseData,
  hadleGraphDataAccordingToLimit,
  getPointsCount,
  getVehicleData,
  getGraphGdpr,
  getPointsData,
} from '../GraphService';
import FuelLevelPercChart from './ThrottleChart';
import RideJustEnded from '../RideJustEnded';
import GdprIcon from '../GdprIcon';
import usePrevious from '../../../hooks/usePrevious';
import { graphCurrentPointsDataAtom, IGraphCurrentPointsData } from '../GraphState';
import { IVehicleGdprDataAllowed } from '../../Vehicles/types';
import { isVehicleGdprDataAllowedAtom, vehicleByIdSelector } from '../../../store/recoil/vehicles';
import { onlinePanelForVehicleSelector } from '../../OnlinePanel/OnlinePanelState';

interface IProps {
  vehicleId: number;
  responseData: IGraphResponseData[];
  isCompressed: boolean;
  loadRide: () => void;
  handleEndRideDateAndAddress: (address: string) => void;
  isDetail: boolean;
}

function Driving(props: IProps) {
  const { formatMessage: f } = useIntl();
  const [isRideJustEnded, setIsRideJustEnded] = React.useState(false);
  const [pointsCount, setPointsCount] = React.useState(0);
  const [graphData, setGraphData] = React.useState<IGraphData>({
    xLabels: [],
    yLabels: [],
    tooltips: {},
    minLabels: [],
    maxLabels: [],
  });
  const setGraphCurrentPointsData = useSetRecoilState<IGraphCurrentPointsData[] | null>(
    graphCurrentPointsDataAtom
  );
  const [ignitionOnFirstTs, setIgnitionOnFirstTs] = React.useState<string | null>('');
  const onlinePanel = useRecoilValue(onlinePanelForVehicleSelector(Number(props.vehicleId)));
  // const previousTimestamp = usePrevious(onlinePanel ? String(onlinePanel.timestamp) : '', {
  //   jsonParse: false,
  // });
  const [isNotAllowedByGdpr, setIsNotAllowedByGdpr] = React.useState(false);
  const vehicle = useRecoilValue(vehicleByIdSelector(props.vehicleId));
  const previousDrivingMode = usePrevious(vehicle?.drivingMode, { jsonParse: false });
  const isVehicleGdprDataAllowed = useRecoilValue<IVehicleGdprDataAllowed>(
    isVehicleGdprDataAllowedAtom
  );

  const getData = () => {
    const xLabels: string[] = [];
    const yLabels: (number | null)[] = [];
    const minLabels: string[] = [];
    const maxLabels: string[] = [];
    const tooltips = {};
    if (props.responseData) {
      setPointsCount(getPointsCount(props.responseData, ignitionOnFirstTs));
      props.responseData.forEach((i) => {
        if (!xLabels.includes(i.timestamp)) {
          xLabels.push(i.timestamp);
          yLabels.push(i.value);
          minLabels.push(i.min);
          maxLabels.push(i.max);
          tooltips[i.timestamp] = {
            address: i.address || `${i.latitude}, ${i.longitude}`,
            code: i.code,
          };
        }
      });
    }
    setGraphData({
      xLabels,
      yLabels,
      minLabels,
      maxLabels,
      tooltips,
    });
  };

  React.useEffect(() => {
    if (graphData?.xLabels && graphData.xLabels.length > 0) {
      const pointData = getPointsData(graphData);
      setGraphCurrentPointsData(pointData);
    }
  }, [graphData, setGraphCurrentPointsData]);

  React.useEffect(() => {
    if (props.responseData && ignitionOnFirstTs !== '') {
      getData();
      setIsRideJustEnded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.responseData, ignitionOnFirstTs]);

  const handleVehicleData = async () => {
    const res = await getVehicleData(props.vehicleId);
    if (res !== false) {
      setIgnitionOnFirstTs(res);
    }
  };

  useQueries({
    queries: [
      {
        queryKey: ['graphVehicleData', props.vehicleId],
        queryFn: () => handleVehicleData(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  React.useEffect(() => {
    if (
      !isRideJustEnded &&
      props.vehicleId &&
      onlinePanel &&
      !graphData.xLabels.includes(onlinePanel.timestamp) &&
      new Date(graphData.xLabels[graphData.xLabels.length - 1]) < new Date(onlinePanel.timestamp)
      // previousTimestamp &&
      // String(onlinePanel.timestamp) !== previousTimestamp
    ) {
      if (onlinePanel.throttle !== null) {
        let tempGraphData = { ...graphData };
        // if (
        //   new Date(graphData.xLabels[graphData.xLabels.length - 1]).getTime() >
        //   new Date(onlinePanel.timestamp).getTime()
        // ) {
        //   const timestamps: string[] = [];
        //   const yData: (number | null)[] = [];
        //   const tooltipsData = {};
        //   const labels = graphData.xLabels;
        //   let key = 0;

        //   while (new Date(labels[key]).getTime() < new Date(onlinePanel.timestamp).getTime()) {
        //     if (labels.length > GRAPH_LIMIT) {
        //       if (key !== 0) {
        //         timestamps.push(labels[key]);
        //         yData.push(graphData.yLabels[key]);
        //         tooltipsData[key - 1] = graphData.tooltips[key];
        //       }
        //       key += 1;
        //     } else {
        //       timestamps.push(labels[key]);
        //       yData.push(graphData.yLabels[key]);
        //       tooltipsData[key] = graphData.tooltips[key];
        //       key += 1;
        //     }
        //   }
        //   timestamps.push(onlinePanel.timestamp);
        //   yData.push(onlinePanel.throttle);
        //   tooltipsData[key] = {
        //     address: onlinePanel.address || `${onlinePanel.latitude}, ${onlinePanel.longitude}`,
        //   };
        //   labels.forEach((item: string, oldKey: number) => {
        //     if (oldKey >= key) {
        //       timestamps.push(item);
        //       yData.push(graphData.yLabels[oldKey]);
        //       tooltipsData[oldKey + 1] = graphData.tooltips[oldKey];
        //     }
        //   });
        //   setGraphData({
        //     xLabels: getLimitedValues(timestamps),
        //     yLabels: getLimitedNumbers(yData),
        //     minLabels: getLimitedValues([...graphData.minLabels]),
        //     maxLabels: getLimitedValues([...graphData.maxLabels]),
        //     tooltips: getLimitedObjectValues(tooltipsData),
        //   });
        // } else {

        tempGraphData.xLabels.push(onlinePanel.timestamp);
        tempGraphData.yLabels.push(onlinePanel.throttle);
        tempGraphData.tooltips[onlinePanel.timestamp] = {
          address: onlinePanel.address || `${onlinePanel.latitude}, ${onlinePanel.longitude}`,
          code: onlinePanel.code,
        };

        tempGraphData = hadleGraphDataAccordingToLimit(tempGraphData, pointsCount + 1);

        setPointsCount(pointsCount + 1);
        setGraphData(tempGraphData);
        // }
      }
    }
  }, [props.vehicleId, graphData, onlinePanel, isRideJustEnded, pointsCount]);

  const handleRideJustEnded = () => {
    setIsRideJustEnded(true);
  };

  React.useEffect(() => {
    if (
      !isRideJustEnded &&
      vehicle?.drivingMode &&
      previousDrivingMode &&
      vehicle?.drivingMode !== previousDrivingMode
    ) {
      if (vehicle?.drivingMode === 'private') {
        if (!getGraphGdpr(isVehicleGdprDataAllowed, vehicle.id, f)) {
          setIsNotAllowedByGdpr(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousDrivingMode, vehicle?.drivingMode]);

  return !isNotAllowedByGdpr ? (
    <div>
      {!props.isCompressed && pointsCount > GRAPH_LIMIT && (
        <div style={{ padding: props.isDetail ? '20px 0 0 0' : '' }}>
          <CmdAlert
            id="velocity-graph-alert"
            data={{
              title: f({ id: 'graph.velocity.limitAlert.title' }),
              message: f({ id: 'graph.graphLimit.alert.message' }),
              type: 'info',
            }}
            noCancel
          />
        </div>
      )}
      <RideJustEnded
        vehicleId={props.vehicleId}
        handleEndRideDateAndAddress={props.handleEndRideDateAndAddress}
        loadData={props.loadRide}
        pointsCount={pointsCount}
        isRideJustEnded={handleRideJustEnded}
      />
      <FuelLevelPercChart
        graphData={graphData}
        noAnimation="animation"
        isCompressed={props.isCompressed}
        isOnline
      />
    </div>
  ) : (
    <GdprIcon />
  );
}

export default React.memo(Driving);
